import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { SharedModule } from '@shared/shared.module';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { map } from 'rxjs';
import {
  HD_LIFE_TIMELINE_PX_PER_YEAR,
  HD_LIFE_TIMELINE_ZOOMS,
  HdLifeTimelineMarker,
  HdLifeTimelineViewport,
  HdLifeTimelineZoom,
  HdLifeTransitLane,
} from './hd-life-timeline.models';
import { HD_VITALITY_ICONS, hdEmotionLabelKey, hdMoodColor } from '../bodygraph/advanced-properties/hd-moment-fields';
import { HdTimelineHelpComponent, HdTimelineHelpEntry } from './hd-timeline-help.component';

const YEAR_MS = 365.25 * 24 * 3_600_000;
/** Een range blijft ook uitgezoomd zichtbaar als balkje. */
const RANGE_MIN_PX = 6;
/** Marge links van het domein-begin en rechts van het domein-einde. */
const DOMAIN_MARGIN_MS = YEAR_MS;
/**
 * Hoofdbereik: van de geboorte tot ±84 jaar (de Uranus-return, de laatste vaste
 * levenscyclus), of tot vandaag als dat later valt. Het startbeeld kiest zijn
 * schaal op dit bereik; markers daarbuiten (vóór de geboorte, Jupiter-returns
 * tot ±107 jaar) verbreden enkel het domein, je scrolt ernaar (Steven 2026-10-29).
 */
const FOCUS_YEARS = 84;
/** Halve stipbreedte in de rij-toewijzing; dekt ook het touch-formaat (36 px). */
const DOT_HALF_PX = 18;
/** Horizontale ademruimte tussen items binnen dezelfde rij. */
const LANE_GAP_PX = 8;
/** Mini-labels: maximale breedte, daarboven ellipsis. */
const LABEL_MAX_PX = 140;
/** measureText-fallback (geen canvas-context beschikbaar). */
const LABEL_FALLBACK_CHAR_PX = 6;
const LABEL_FONT = '11px sans-serif';
/** Ruimte boven (jaarlabels) en onder (leeftijdslabels) de rijen. */
const TOP_PAD_PX = 20;
const BOTTOM_PAD_PX = 20;
/** Eigen rij voor de berekende cycli, direct onder de as (beslispunt 6). */
const SYSTEM_ROW_HEIGHT_PX = 34;
const MIN_TRACK_HEIGHT_PX = 150;
/** Transit-strip (spec §2.5): compacte rij per planeet onder de momentenrijen. */
const STRIP_LANE_HEIGHT_PX = 18;
const STRIP_TOP_GAP_PX = 6;
const STRIP_BLOCK_MIN_PX = 2;
/** Scroll-emissies voor de strip-datalader, trailing-throttled. */
const VIEWPORT_EMIT_THROTTLE_MS = 250;
/** Scroll-stap van de pijltjestoetsen (zelfde als de transits-lanes). */
const KEY_SCROLL_PX = 240;
/** Sleepafstand (px) waarboven een pointer-drag als pan telt i.p.v. als klik. */
const DRAG_PAN_THRESHOLD_PX = 4;

/** Item in het rijen-layout: stip (widthPx null) of range-balkje. */
interface ItemVm {
  readonly marker: HdLifeTimelineMarker;
  readonly xPx: number;
  readonly widthPx: number | null;
  readonly laneIndex: number;
  /** Mini-label boven het item; null wanneer labels uitstaan. */
  readonly label: string | null;
}

interface CycleVm {
  readonly marker: HdLifeTimelineMarker;
  readonly xPx: number;
}

interface TickVm {
  readonly xPx: number;
  /** Hoofdlabel (jaartal, maandnaam of dagnummer); null = kale rasterlijn. */
  readonly label: string | null;
  /** Leeftijd op de jaargrens (label via i18n `lifeTimeline.ageShort`); null = geen. */
  readonly age: number | null;
  /** Ondergeschikte tick: lichtere lijn en klein label. */
  readonly minor: boolean;
}

/**
 * Levenstijdlijn (spec momenten stap 5; rijen-model uit
 * `docs/LEVENSTIJDLIJN.md` §2.1): één horizontale, lineaire tijd-as
 * (px-per-ms, zoals de ephemeris-lanes maar zelfstandig) van vóór de geboorte
 * tot voorbij vandaag. Overlappende momenten/ranges krijgen elk een eigen rij
 * (greedy interval-partitioning in pixelruimte) zodat alles apart klikbaar is;
 * cycli staan op een eigen systeemrij onder de as. Mini-labels (icoon zit in de
 * stip, titel erboven) zijn schakelbaar en tellen mee in de rij-toewijzing.
 * Pannen = native horizontale scroll én muis-slepen; ctrl+scroll zoomt.
 * Dom presentatiecomponent: de pagina levert markers en handelt
 * openen/toevoegen af.
 */
@Component({
  selector: 'hd-life-timeline',
  standalone: true,
  imports: [SharedModule, TranslocoModule, HdTimelineHelpComponent],
  templateUrl: './hd-life-timeline.component.html',
  styleUrl: './hd-life-timeline.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLifeTimelineComponent {
  readonly birthUtc = input.required<string>();
  readonly markers = input.required<readonly HdLifeTimelineMarker[]>();
  /** Mini-labels boven de items (spec §2.8); de pagina persisteert de keuze. */
  readonly showLabels = model(true);
  /**
   * Kleurmodus (spec §2.3): 'moment' kleurt stip/balk met de momentkleur,
   * 'mood' met de stemming-gradiënt — dat maakt zware en lichte periodes in
   * één oogopslag zichtbaar. De pagina persisteert de keuze.
   */
  readonly colorMode = model<'moment' | 'mood'>('moment');
  /**
   * Transit-strip onder de momentenrijen (spec §2.5): de pagina laadt de
   * poort-blokken per planeet op basis van {@link viewportChanged}. De strip
   * rendert alleen op zoom `year`/`month` (grovere zooms zijn streepjeswerk).
   */
  readonly transitLanes = input<readonly HdLifeTransitLane[]>([]);

  /** Klik op "Openen" in de popover. */
  readonly openRequested = output<string>();
  /** "Openen" op een I Tjing-moment: de raadpleging (host opent de I Tjing-momentdialoog). */
  readonly consultationOpenRequested = output<string>();
  /** Potlood in de popover: momentdetails bewerken (finetune 2026-09-21). */
  readonly editRequested = output<string>();
  /** Klik op een leeg stuk as: moment toevoegen op dit tijdstip. */
  readonly addRequested = output<string>();
  /**
   * Zichtbaar tijdvenster (na init, zoomwissel en scroll, throttled): de
   * pagina laadt hierop lazy de transit-segmenten voor de strip.
   */
  readonly viewportChanged = output<HdLifeTimelineViewport>();

  private readonly ngZone = inject(NgZone);
  private readonly transloco = inject(TranslocoService);
  /** Moment-locale van de app-taal (zoals de transloco-labels van de tijdlijn): maandnamen op de as. */
  private readonly locale = toSignal(
    this.transloco.langChanges$.pipe(map(momentLocaleFor)),
    { initialValue: momentLocaleFor(this.transloco.getActiveLang()) },
  );

  protected readonly zoom = signal<HdLifeTimelineZoom>('fiveYears');
  protected readonly zoomLevels = HD_LIFE_TIMELINE_ZOOMS;
  /** Popover-selectie: precies één marker (rijen maken clusters overbodig). */
  protected readonly selection = signal<{ marker: HdLifeTimelineMarker; xPx: number; topPx: number } | null>(null);
  /** true zodra een pointer-drag de pan-drempel passeert (grabbing-cursor). */
  protected readonly panning = signal(false);

  /** Interactieregels voor het hulp-popovertje (gedeeld componentje). */
  protected readonly helpEntries: readonly HdTimelineHelpEntry[] = [
    { icon: 'hand', textKey: 'humanDesign.timelineHelp.drag' },
    { icon: 'arrows-left-right', textKey: 'humanDesign.timelineHelp.shiftScroll' },
    { icon: 'magnifying-glass-plus', textKey: 'humanDesign.timelineHelp.ctrlScroll' },
    { icon: 'plus', textKey: 'humanDesign.timelineHelp.clickAdd' },
    { icon: 'circle-info', textKey: 'humanDesign.timelineHelp.clickMarker' },
  ];

  private readonly scrollerRef = viewChild<ElementRef<HTMLElement>>('scroller');

  /** Grotere tap-targets op touch: rijen en stippen schalen mee (CSS spiegelt dit). */
  private readonly coarsePointer =
    typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
  private readonly dotSizePx = this.coarsePointer ? 36 : 28;

  /**
   * Sleep-pan-administratie (patroon transits-lanes): pointermove wordt alleen
   * tijdens een mogelijke drag gebonden, buiten de Angular-zone.
   */
  private panStart: { pointerId: number; clientX: number; scrollLeftPx: number } | null = null;
  private panMoveCleanup: (() => void) | null = null;
  /** De click die een pan-drag afsluit mag niets selecteren of toevoegen. */
  private panEndedClickPending = false;

  /** Labelbreedtes zijn duur om te meten; memoized per labeltekst. */
  private readonly labelWidthCache = new Map<string, number>();
  private labelCtx: CanvasRenderingContext2D | null | undefined;

  private readonly birthMs = computed(() => Date.parse(this.birthUtc()));

  /** Hoofdbereik (zie {@link FOCUS_YEARS}): geboorte t/m ±84 jaar of vandaag. */
  private readonly focusStartMs = computed(() => this.birthMs());
  private readonly focusEndMs = computed(() => Math.max(Date.now(), this.birthMs() + FOCUS_YEARS * YEAR_MS));

  /**
   * Domein: het hoofdbereik plus alle markers, met een jaar marge aan elke kant.
   * Markers vóór de geboorte of na het hoofdbereik maken de track breder, de
   * schaal blijft die van het startbeeld.
   */
  private readonly domainStartMs = computed(() => {
    let start = this.focusStartMs();
    for (const marker of this.markers()) {
      const markerStart = Date.parse(marker.startUtc);
      if (Number.isFinite(markerStart) && markerStart < start) start = markerStart;
    }
    return start - DOMAIN_MARGIN_MS;
  });
  private readonly domainEndMs = computed(() => {
    let end = this.focusEndMs();
    for (const marker of this.markers()) {
      const markerEnd = Date.parse(marker.endUtc ?? marker.startUtc);
      if (Number.isFinite(markerEnd) && markerEnd > end) end = markerEnd;
    }
    return end + DOMAIN_MARGIN_MS;
  });

  /** Gedempte stroken buiten het hoofdbereik (vóór de geboorte en erna). */
  protected readonly outsideFocus = computed(() => {
    const beforeWidthPx = Math.max(0, this.xOf(this.focusStartMs()));
    const afterLeftPx = Math.min(this.trackWidthPx(), this.xOf(this.focusEndMs()));
    return {
      beforeWidthPx,
      afterLeftPx,
      afterWidthPx: Math.max(0, this.trackWidthPx() - afterLeftPx),
    };
  });

  private readonly pxPerMs = computed(() => HD_LIFE_TIMELINE_PX_PER_YEAR[this.zoom()] / YEAR_MS);

  protected readonly trackWidthPx = computed(() =>
    Math.max(1, Math.round((this.domainEndMs() - this.domainStartMs()) * this.pxPerMs())));

  protected readonly todayXPx = computed(() => this.xOf(Date.now()));

  /**
   * As-indeling per zoomniveau (finetune 2026-09-21): decennium/5 jaar tonen
   * jaren, jaar-zoom toont maanden (kwartaallabels, jaargrens = jaartal),
   * maand-zoom toont maanden mét weekdagen (dag 8/15/22). Leeftijd ("38 j")
   * blijft op de jaargrenzen als vaste onderas (beslispunt 3).
   */
  protected readonly ticks = computed<TickVm[]>(() => {
    const zoom = this.zoom();
    const locale = this.locale();
    const birth = moment(this.birthMs());
    const startMs = this.domainStartMs();
    const endMs = this.domainEndMs();
    const result: TickVm[] = [];
    const pushYear = (tick: moment.Moment, withLabel: boolean): void => {
      const age = tick.diff(birth, 'years');
      result.push({
        xPx: this.xOf(tick.valueOf()),
        label: withLabel ? String(tick.year()) : null,
        age: withLabel && age >= 0 ? age : null,
        minor: !withLabel,
      });
    };

    if (zoom === 'decade' || zoom === 'fiveYears') {
      // Jaarraster; op decennium-zoom een label per 5 jaar (±150 px ruimte).
      const firstYear = moment(startMs).year();
      const lastYear = moment(endMs).year();
      for (let year = firstYear; year <= lastYear; year++) {
        const tick = moment({ year, month: 0, day: 1 });
        if (tick.valueOf() < startMs) continue;
        pushYear(tick, zoom === 'fiveYears' || year % 5 === 0);
      }
    } else if (zoom === 'year') {
      // Maandticks (20 px per maand): labels per kwartaal, jaargrens = jaartal.
      const cursor = moment(startMs).locale(locale).startOf('month');
      while (cursor.valueOf() <= endMs) {
        if (cursor.valueOf() >= startMs) {
          const month = cursor.month();
          if (month === 0) {
            pushYear(cursor.clone(), true);
          } else {
            result.push({
              xPx: this.xOf(cursor.valueOf()),
              label: month % 3 === 0 ? cursor.format('MMM') : null,
              age: null,
              minor: month % 3 !== 0,
            });
          }
        }
        cursor.add(1, 'month');
      }
    } else {
      // Maand-zoom (±120 px per maand): maandlabels + weekticks met dagnummer.
      const cursor = moment(startMs).locale(locale).startOf('month');
      while (cursor.valueOf() <= endMs) {
        if (cursor.valueOf() >= startMs) {
          const isYearStart = cursor.month() === 0;
          const age = cursor.diff(birth, 'years');
          result.push({
            xPx: this.xOf(cursor.valueOf()),
            label: cursor.format(isYearStart ? 'MMM YYYY' : 'MMM'),
            age: isYearStart && age >= 0 ? age : null,
            minor: false,
          });
          for (const day of [8, 15, 22]) {
            result.push({
              xPx: this.xOf(cursor.clone().date(day).valueOf()),
              label: String(day),
              age: null,
              minor: true,
            });
          }
        }
        cursor.add(1, 'month');
      }
    }
    return result;
  });

  /**
   * Rijen-layout (spec §2.1): momenten + ranges gesorteerd op start-x, greedy
   * toegewezen aan de eerste rij zonder overlap. Het bezette interval telt de
   * stipbreedte, de balkbreedte én (met labels aan) de labelbreedte mee, zodat
   * niets elkaar raakt. Herrekent per zoomniveau (pixelruimte).
   */
  protected readonly layout = computed<{ items: ItemVm[]; laneCount: number }>(() => {
    const showLabels = this.showLabels();
    const entries = this.markers()
      .filter(marker => marker.kind !== 'cycle')
      .map(marker => {
        const startX = this.xOf(Date.parse(marker.startUtc));
        const isRange = marker.kind === 'range' && !!marker.endUtc;
        const widthPx = isRange
          ? Math.max(RANGE_MIN_PX, this.xOf(Date.parse(marker.endUtc!)) - startX)
          : null;
        return { marker, startX, widthPx };
      })
      .filter(entry => Number.isFinite(entry.startX))
      .sort((a, b) => a.startX - b.startX);

    const laneEnds: number[] = [];
    const items: ItemVm[] = [];
    for (const entry of entries) {
      const renderStart = entry.widthPx == null ? entry.startX - DOT_HALF_PX : entry.startX;
      const renderEnd = entry.widthPx == null ? entry.startX + DOT_HALF_PX : entry.startX + entry.widthPx;
      const label = showLabels ? entry.marker.label : null;
      const occupiedEnd = label != null
        ? Math.max(renderEnd, renderStart + this.measureLabel(label))
        : renderEnd;
      let laneIndex = laneEnds.findIndex(end => end + LANE_GAP_PX <= renderStart);
      if (laneIndex === -1) {
        laneIndex = laneEnds.length;
        laneEnds.push(occupiedEnd);
      } else {
        laneEnds[laneIndex] = occupiedEnd;
      }
      items.push({ marker: entry.marker, xPx: entry.startX, widthPx: entry.widthPx, laneIndex, label });
    }
    return { items, laneCount: laneEnds.length };
  });

  /** Berekende cycli op de eigen systeemrij onder de as (beslispunt 6). */
  protected readonly cycleDots = computed<CycleVm[]>(() =>
    this.markers()
      .filter(marker => marker.kind === 'cycle')
      .map(marker => ({ marker, xPx: this.xOf(Date.parse(marker.startUtc)) }))
      .filter(entry => Number.isFinite(entry.xPx))
      .sort((a, b) => a.xPx - b.xPx));

  protected readonly systemRowVisible = computed(() => this.cycleDots().length > 0);

  protected readonly rowHeightPx = computed(() => {
    const labeled = this.showLabels();
    if (this.coarsePointer) return labeled ? 58 : 42;
    return labeled ? 48 : 34;
  });

  protected readonly lanesTopPx = computed(() =>
    TOP_PAD_PX + (this.systemRowVisible() ? SYSTEM_ROW_HEIGHT_PX : 0));

  /** Strip alleen op de fijne zooms: op decennium/5 jaar is zelfs Saturnus streepjeswerk. */
  protected readonly stripVisible = computed(() =>
    (this.zoom() === 'year' || this.zoom() === 'month') && this.transitLanes().length > 0);

  protected readonly stripTopPx = computed(() =>
    this.lanesTopPx() + this.layout().laneCount * this.rowHeightPx() + STRIP_TOP_GAP_PX);

  protected readonly stripHeightPx = computed(() =>
    this.stripVisible() ? this.transitLanes().length * STRIP_LANE_HEIGHT_PX : 0);

  /** Trackhoogte groeit met het aantal rijen (en de strip) mee (onbeperkt, beslispunt 5). */
  protected readonly trackHeightPx = computed(() => Math.max(
    MIN_TRACK_HEIGHT_PX,
    this.stripTopPx() + this.stripHeightPx() + BOTTOM_PAD_PX,
  ));

  constructor() {
    // Startbeeld: het hoofdbereik zo groot mogelijk in één oogopslag. Het
    // fijnste zoomniveau waarop geboorte t/m ±84 jaar (plus marges) past, anders
    // het grofste; links begint het beeld net vóór de geboorte. Wat daarbuiten
    // valt, drukt de schaal niet samen maar staat ernaast (scrollen).
    afterNextRender(() => {
      const scroller = this.scrollerRef()?.nativeElement;
      const viewportPx = scroller?.clientWidth || 1200;
      const focusMs = this.focusEndMs() - this.focusStartMs() + 2 * DOMAIN_MARGIN_MS;
      const fitting = [...HD_LIFE_TIMELINE_ZOOMS]
        .reverse()
        .find(level => focusMs * (HD_LIFE_TIMELINE_PX_PER_YEAR[level] / YEAR_MS) <= viewportPx);
      this.zoom.set(fitting ?? 'decade');
      if (scroller == null) return;
      const targetLeft = Math.max(0, this.xOf(this.focusStartMs() - DOMAIN_MARGIN_MS));
      this.applyScrollLeftAfterRender(scroller, targetLeft);
    });

    // Schuift het domein-begin (bv. een moment vóór de geboorte komt binnen of
    // valt weg door een filter), dan schuift de scrollpositie mee: het beeld
    // blijft op dezelfde datum staan in plaats van te verspringen.
    effect(() => {
      const startMs = this.domainStartMs();
      const previousMs = this.lastDomainStartMs;
      this.lastDomainStartMs = startMs;
      const scroller = untracked(() => this.scrollerRef()?.nativeElement);
      if (previousMs == null || previousMs === startMs || scroller == null) return;
      const shiftPx = (previousMs - startMs) * untracked(() => this.pxPerMs());
      this.applyScrollLeftAfterRender(scroller, Math.max(0, scroller.scrollLeft + shiftPx));
    });

    // Wheel-listener non-passive en buiten de zone (patroon transits-lanes):
    // ctrl+wheel moet de browser-zoom onderdrukken en mag niet per event een
    // app-brede change detection kosten.
    effect(onCleanup => {
      const element = this.scrollerRef()?.nativeElement;
      if (element == null) return;
      const wheelListener = (event: WheelEvent): void => this.onWheel(event);
      this.ngZone.runOutsideAngular(() => element.addEventListener('wheel', wheelListener));
      onCleanup(() => element.removeEventListener('wheel', wheelListener));
    });

    // Scroll → viewport-emissie voor de strip-datalader (passief, buiten de
    // zone, trailing-throttled; alleen de emit gaat de zone weer in).
    effect(onCleanup => {
      const element = this.scrollerRef()?.nativeElement;
      if (element == null) return;
      const scrollListener = (): void => this.scheduleViewportEmit();
      this.ngZone.runOutsideAngular(() => element.addEventListener('scroll', scrollListener, { passive: true }));
      onCleanup(() => {
        element.removeEventListener('scroll', scrollListener);
        if (this.viewportEmitTimer != null) {
          clearTimeout(this.viewportEmitTimer);
          this.viewportEmitTimer = null;
        }
      });
    });
  }

  private viewportEmitTimer: ReturnType<typeof setTimeout> | null = null;
  /** Vorig domein-begin, om de scrollpositie mee te schuiven bij een verschuiving. */
  private lastDomainStartMs: number | null = null;

  private scheduleViewportEmit(): void {
    if (this.viewportEmitTimer != null) return;
    this.viewportEmitTimer = setTimeout(() => {
      this.viewportEmitTimer = null;
      this.emitViewport();
    }, VIEWPORT_EMIT_THROTTLE_MS);
  }

  private emitViewport(): void {
    const scroller = this.scrollerRef()?.nativeElement;
    if (scroller == null) return;
    const pxPerMs = this.pxPerMs();
    const fromMs = this.domainStartMs() + scroller.scrollLeft / pxPerMs;
    const toMs = fromMs + Math.max(scroller.clientWidth, 1) / pxPerMs;
    this.ngZone.run(() => this.viewportChanged.emit({ fromMs, toMs, zoom: this.zoom() }));
  }

  protected setZoom(level: HdLifeTimelineZoom, anchorClientX?: number): void {
    if (level === this.zoom()) return;
    const scroller = this.scrollerRef()?.nativeElement;
    if (scroller == null) {
      this.zoom.set(level);
      return;
    }
    // Anker (finetune 2026-09-21): de tijd onder de cursor (ctrl+scroll) of
    // het zichtbare midden (zoomknoppen) blijft op dezelfde schermpositie.
    const anchorOffsetPx = anchorClientX != null
      ? Math.max(0, Math.min(scroller.clientWidth, anchorClientX - scroller.getBoundingClientRect().left))
      : scroller.clientWidth / 2;
    const anchorMs = this.domainStartMs() + (scroller.scrollLeft + anchorOffsetPx) / this.pxPerMs();
    this.zoom.set(level);
    const targetLeft = Math.max(0, (anchorMs - this.domainStartMs()) * this.pxPerMs() - anchorOffsetPx);
    this.applyScrollLeftAfterRender(scroller, targetLeft);
  }

  /**
   * Scroll pas toepassen zodra de nieuwe trackbreedte gerenderd is: direct na
   * een zoomwissel clampt de browser anders op de oude scrollbreedte en
   * "springt" de tijdlijn naar het begin. Kort rAF-pollen tot de scroller de
   * positie kan dragen (of na een handvol frames gewoon toepassen).
   */
  private applyScrollLeftAfterRender(scroller: HTMLElement, targetLeft: number, attempt = 0): void {
    const canApply = scroller.scrollWidth - scroller.clientWidth >= targetLeft - 1;
    if (canApply || attempt >= 10 || typeof requestAnimationFrame === 'undefined') {
      scroller.scrollTo({ left: targetLeft });
      this.scheduleViewportEmit();
      return;
    }
    requestAnimationFrame(() => this.applyScrollLeftAfterRender(scroller, targetLeft, attempt + 1));
  }

  protected zoomLabelKey(level: HdLifeTimelineZoom): string {
    return `humanDesign.lifeTimeline.zoom.${level}`;
  }

  // ── Posities voor de template ──────────────────────────────────────────────

  protected dotTopPx(item: ItemVm): number {
    return this.rowTopPx(item.laneIndex) + (this.showLabels() ? 17 : 3);
  }

  protected barTopPx(item: ItemVm): number {
    // Balkhoogte 14 px (touch 20 px, zie SCSS): verticaal gecentreerd op de stip.
    return this.dotTopPx(item) + (this.dotSizePx - (this.coarsePointer ? 20 : 14)) / 2;
  }

  protected labelTopPx(item: ItemVm): number {
    return this.rowTopPx(item.laneIndex) + 2;
  }

  protected labelLeftPx(item: ItemVm): number {
    return item.widthPx == null ? item.xPx - DOT_HALF_PX + 4 : item.xPx;
  }

  /** Inline-achtergrond van stip/balk; null = de default CSS-stijl. */
  protected itemColor(item: ItemVm): string | null {
    if (this.colorMode() === 'mood') return hdMoodColor(item.marker.mood);
    return item.marker.colorHex ?? null;
  }

  protected readonly moodColor = hdMoodColor;
  protected readonly vitalityIcons = HD_VITALITY_ICONS;
  protected readonly emotionLabelKey = hdEmotionLabelKey;

  // ── Transit-strip (spec §2.5) ──────────────────────────────────────────────

  protected stripBlockLeftPx(block: { fromMs: number }): number {
    return Math.max(0, this.xOf(block.fromMs));
  }

  protected stripBlockWidthPx(block: { fromMs: number; toMs: number }): number {
    const left = this.stripBlockLeftPx(block);
    const right = Math.min(this.trackWidthPx(), this.xOf(block.toMs));
    return Math.max(STRIP_BLOCK_MIN_PX, right - left);
  }

  protected stripBlockTitle(block: { fromMs: number; toMs: number; gate: number }): string {
    const formatter = new Intl.DateTimeFormat('nl-BE', { dateStyle: 'medium' });
    return `${block.gate} · ${formatter.format(new Date(block.fromMs))} tot ${formatter.format(new Date(block.toMs))}`;
  }

  protected cycleTopPx(): number {
    return TOP_PAD_PX + 3;
  }

  private rowTopPx(laneIndex: number): number {
    return this.lanesTopPx() + laneIndex * this.rowHeightPx();
  }

  // ── Selectie / popover ─────────────────────────────────────────────────────

  /**
   * Popover-anker (finetune 2026-09-21): bij de klikpositie en altijd binnen
   * het zichtbare venster — een range waarvan het begin buiten beeld ligt
   * krijgt zijn details anders onbereikbaar links van de viewport.
   */
  private popoverAnchorX(event: Event, fallbackX: number): number {
    const scroller = this.scrollerRef()?.nativeElement;
    const mouse = event as MouseEvent;
    let x = fallbackX;
    if (scroller != null && typeof mouse.clientX === 'number' && mouse.clientX !== 0) {
      x = scroller.scrollLeft + (mouse.clientX - scroller.getBoundingClientRect().left);
    }
    if (scroller == null) return x;
    const min = scroller.scrollLeft + 8;
    const max = scroller.scrollLeft + Math.max(scroller.clientWidth - 260, 16);
    return Math.min(Math.max(x, min), max);
  }

  protected selectItem(item: ItemVm, event: Event): void {
    event.stopPropagation();
    if (this.consumePanEndedClick()) return;
    this.selection.set({
      marker: item.marker,
      xPx: this.popoverAnchorX(event, item.xPx),
      topPx: this.dotTopPx(item) + this.dotSizePx + 4,
    });
  }

  protected selectCycle(cycle: CycleVm, event: Event): void {
    event.stopPropagation();
    if (this.consumePanEndedClick()) return;
    this.selection.set({
      marker: cycle.marker,
      xPx: this.popoverAnchorX(event, cycle.xPx),
      topPx: this.cycleTopPx() + 32,
    });
  }

  /** Openen: een chart, of bij een I Tjing-moment de raadpleging (dialoog in de host). */
  protected openMarker(marker: HdLifeTimelineMarker): void {
    if (marker.consultationId) {
      this.selection.set(null);
      this.consultationOpenRequested.emit(marker.consultationId);
      return;
    }
    if (!marker.openChartId) return;
    this.selection.set(null);
    this.openRequested.emit(marker.openChartId);
  }

  /** Potlood in de popover: momentdetails bewerken (host opent de editdialoog). */
  protected editMarker(marker: HdLifeTimelineMarker): void {
    if (!marker.openChartId) return;
    this.selection.set(null);
    this.editRequested.emit(marker.openChartId);
  }

  /** Klik op de lege as: moment toevoegen op dit tijdstip (markers stoppen hun eigen klik). */
  protected onTrackClick(event: MouseEvent): void {
    if (this.consumePanEndedClick()) return;
    if (this.selection() != null) {
      this.selection.set(null);
      return;
    }
    const track = event.currentTarget as HTMLElement;
    const xPx = event.clientX - track.getBoundingClientRect().left;
    const clickedMs = this.domainStartMs() + xPx / this.pxPerMs();
    this.addRequested.emit(new Date(Math.round(clickedMs)).toISOString());
  }

  /** Weergavetekst van één marker in de popover: datum(bereik) + leeftijd op dat moment. */
  protected markerMomentText(marker: HdLifeTimelineMarker): string {
    const formatter = new Intl.DateTimeFormat('nl-BE', { dateStyle: 'medium', timeStyle: 'short' });
    const start = new Date(Date.parse(marker.startUtc));
    const base = marker.endUtc
      ? `${formatter.format(start)} tot ${formatter.format(new Date(Date.parse(marker.endUtc)))}`
      : formatter.format(start);
    const age = moment(start.getTime()).diff(moment(this.birthMs()), 'years');
    return age >= 0 ? `${base} (${age} j)` : base;
  }

  // ── Pan / toetsen / wheel (patroon transits-lanes) ─────────────────────────

  /**
   * Slepen met de muis pant de tijdlijn horizontaal. Touch/pen scrollen al
   * native. Pas voorbij {@link DRAG_PAN_THRESHOLD_PX} telt het als pan; tot dan
   * blijft het een gewone klik.
   */
  protected onPanPointerDown(event: PointerEvent): void {
    this.panning.set(false);
    this.panEndedClickPending = false;
    this.panStart = null;
    this.removePanMoveListener();
    if (event.button !== 0 || event.pointerType !== 'mouse') return;
    const element = this.scrollerRef()?.nativeElement;
    if (element == null) return;
    this.panStart = { pointerId: event.pointerId, clientX: event.clientX, scrollLeftPx: element.scrollLeft };
    const moveListener = (moveEvent: PointerEvent): void => this.onPanPointerMove(moveEvent);
    this.ngZone.runOutsideAngular(() => element.addEventListener('pointermove', moveListener));
    this.panMoveCleanup = () => element.removeEventListener('pointermove', moveListener);
  }

  protected onPanPointerMove(event: PointerEvent): void {
    const start = this.panStart;
    if (start == null || event.pointerId !== start.pointerId) return;
    const element = this.scrollerRef()?.nativeElement;
    if (element == null) return;
    const deltaX = event.clientX - start.clientX;
    if (!this.panning()) {
      if (Math.abs(deltaX) < DRAG_PAN_THRESHOLD_PX) return;
      this.ngZone.run(() => this.panning.set(true));
      try {
        element.setPointerCapture(start.pointerId);
      } catch {
        // Synthetische pointers (specs) hebben geen capture; pannen werkt ook zonder.
      }
    }
    event.preventDefault(); // geen tekstselectie tijdens het slepen
    element.scrollLeft = Math.max(0, start.scrollLeftPx - deltaX);
  }

  protected onPanPointerUp(event: PointerEvent): void {
    const start = this.panStart;
    if (start == null || event.pointerId !== start.pointerId) return;
    this.removePanMoveListener();
    const element = this.scrollerRef()?.nativeElement;
    if (element?.hasPointerCapture(start.pointerId)) element.releasePointerCapture(start.pointerId);
    this.panStart = null;
    // Enkel de click die op deze pointerup volgt wordt nog onderdrukt.
    this.panEndedClickPending = this.panning();
    this.panning.set(false);
  }

  protected onScrollKeydown(event: KeyboardEvent): void {
    const element = this.scrollerRef()?.nativeElement;
    if (element == null) return;
    if (event.key === 'ArrowLeft') {
      element.scrollBy({ left: -KEY_SCROLL_PX });
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      element.scrollBy({ left: KEY_SCROLL_PX });
      event.preventDefault();
    }
  }

  /** Ctrl+scroll stapt door de zoomtrap, geankerd op de cursorpositie. */
  private onWheel(event: WheelEvent): void {
    if (!event.ctrlKey) return;
    event.preventDefault();
    const index = this.zoomLevels.indexOf(this.zoom());
    const next = event.deltaY > 0 ? index - 1 : index + 1;
    if (next < 0 || next >= this.zoomLevels.length) return;
    this.ngZone.run(() => this.setZoom(this.zoomLevels[next], event.clientX));
  }

  /** `true` wanneer deze click enkel het einde van een pan-drag is (eenmalig). */
  private consumePanEndedClick(): boolean {
    if (!this.panEndedClickPending) return false;
    this.panEndedClickPending = false;
    return true;
  }

  private removePanMoveListener(): void {
    this.panMoveCleanup?.();
    this.panMoveCleanup = null;
  }

  private measureLabel(label: string): number {
    const cached = this.labelWidthCache.get(label);
    if (cached != null) return cached;
    if (this.labelCtx === undefined) {
      this.labelCtx = typeof document !== 'undefined'
        ? document.createElement('canvas').getContext('2d')
        : null;
      if (this.labelCtx != null) this.labelCtx.font = LABEL_FONT;
    }
    const measured = this.labelCtx?.measureText(label).width ?? label.length * LABEL_FALLBACK_CHAR_PX;
    const width = Math.min(LABEL_MAX_PX, Math.ceil(measured) + 4);
    this.labelWidthCache.set(label, width);
    return width;
  }

  private xOf(ms: number): number {
    return (ms - this.domainStartMs()) * this.pxPerMs();
  }
}
